import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Plus, Trash2, Edit, KeyRound, Store, User, Printer } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

export default function StoresUsersManagement() {
  const [stores, setStores] = useState([]);
  const [storeUsers, setStoreUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    tel: "",
    contact_person: "",
    username: "",
    password: "",
    multi_column_mode: false,
    column_names: [],
    delivery_schedule_type: "default",
    delivery_weekdays: [],
    excluded_weekdays: [],
    additional_dates: [],
    excluded_dates: [],
    order_deadline_weekday: 1,
    order_deadline_time: "18:00",
    customer_rank: "a1",
  });

  const [pwDialogOpen, setPwDialogOpen] = useState(false);
  const [pwTarget, setPwTarget] = useState(null);
  const [pwValue, setPwValue] = useState("");

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [stRes, usrRes] = await Promise.all([
        axios.get(`${API}/stores`, getAuthHeaders()),
        axios.get(`${API}/stores/users`, getAuthHeaders()),
      ]);
      setStores(stRes.data || []);
      setStoreUsers(usrRes.data || []);
    } catch (error) {
      toast.error("データの取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditing(null);
    setFormData({
      name: "",
      code: "",
      tel: "",
      contact_person: "",
      username: "",
      password: "",
      multi_column_mode: false,
      column_names: [],
      customer_rank: "a1",
    });
    setDialogOpen(true);
  };

  const openEditDialog = (store) => {
    const userInfo = storeUsers.find((u) => u.store_id === store.id);
    setEditing(store);
    setFormData({
      name: store.name || "",
      code: store.code || "",
      tel: store.tel || "",
      contact_person: store.contact_person || "",
      username: userInfo?.username || "",
      password: "",
      multi_column_mode: store.multi_column_mode || false,
      column_names: store.column_names || [],
      delivery_schedule_type: store.delivery_schedule_type || "default",
      delivery_weekdays: store.delivery_weekdays || [],
      excluded_weekdays: store.excluded_weekdays || [],
      additional_dates: store.additional_dates || [],
      excluded_dates: store.excluded_dates || [],
      additional_delivery_days: store.additional_delivery_days || [],
      skip_delivery_days: store.skip_delivery_days || [],
      order_deadline_weekday: store.order_deadline_weekday ?? 1,
      order_deadline_time: store.order_deadline_time || "18:00",
      customer_rank: store.customer_rank || "a1",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.code) {
      toast.error("店舗名とコードは必須です");
      return;
    }

    try {
      if (editing) {
        // 更新
        await axios.put(
          `${API}/stores/${editing.id}`,
          {
            name: formData.name,
            code: formData.code,
            tel: formData.tel,
            contact_person: formData.contact_person,
            multi_column_mode: formData.multi_column_mode,
            column_names: formData.column_names,
            delivery_schedule_type: formData.delivery_schedule_type,
            delivery_weekdays: formData.delivery_weekdays,
            excluded_weekdays: formData.excluded_weekdays,
            additional_dates: formData.additional_dates,
            excluded_dates: formData.excluded_dates,
            additional_delivery_days: formData.additional_delivery_days || [],
            skip_delivery_days: formData.skip_delivery_days || [],
            order_deadline_weekday: formData.order_deadline_weekday,
            order_deadline_time: formData.order_deadline_time,
            customer_rank: formData.customer_rank,
          },
          getAuthHeaders()
        );
        toast.success("店舗情報を更新しました");
      } else {
        // 新規作成
        await axios.post(
          `${API}/stores`,
          {
            name: formData.name,
            code: formData.code,
            tel: formData.tel,
            contact_person: formData.contact_person,
            username: formData.username,
            password: formData.password,
            multi_column_mode: formData.multi_column_mode,
            column_names: formData.column_names,
            customer_rank: formData.customer_rank,
          },
          getAuthHeaders()
        );
        toast.success("店舗を作成しました");
      }
      setDialogOpen(false);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "保存に失敗しました");
    }
  };

  const handleDelete = async (storeId) => {
    if (!window.confirm("本当に削除しますか？")) return;
    try {
      await axios.delete(`${API}/stores/${storeId}`, getAuthHeaders());
      toast.success("削除しました");
      fetchAll();
    } catch (error) {
      toast.error("削除に失敗しました");
    }
  };

  const printLoginLetter = (c) => {
    const esc = (s) => String(s ?? "").replace(/[&<>]/g, (m) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[m]));
    const html = `<!doctype html><html lang="ja"><head><meta charset="utf-8"><title>ログイン通知書</title>
      <style>
        @page { size: A4; margin: 18mm; }
        body { font-family: "Hiragino Kaku Gothic ProN","Yu Gothic","Meiryo",sans-serif; color:#1f2937; }
        h1 { font-size: 18px; text-align:center; border-bottom:2px solid #059669; padding-bottom:8px; margin:0 0 18px; }
        p { font-size: 13px; line-height: 1.9; margin: 8px 0; }
        .box { border:1.5px solid #059669; border-radius:6px; padding:14px 18px; margin:18px 0; }
        .box-title { font-weight:bold; margin-bottom:10px; }
        table { width:100%; border-collapse:collapse; }
        th,td { text-align:left; padding:8px 6px; border-bottom:1px dashed #cbd5e1; font-size:14px; }
        th { width:130px; color:#374151; font-weight:600; }
        .mono { font-family:"Courier New",monospace; font-size:18px; font-weight:bold; letter-spacing:1px; }
        .contact { margin-top:20px; font-size:13px; line-height:1.8; }
      </style></head><body>
      <section>
        <h1>やさまるポータル ログインID・パスワード発行のお知らせ</h1>
        <p>平素より、野菜の丸平をご利用いただき、誠にありがとうございます。</p>
        <p>ウェブ上から簡単にご注文いただける<b>「やさまるポータル」</b>のログイン情報をお知らせいたします。<br/>下記のログインID・パスワードをご確認のうえ、ご利用ください。</p>
        <div class="box">
          <div class="box-title">【 お客様専用ログイン情報 】</div>
          <table>
            <tr><th>お客様名</th><td>${esc(c.store_name)} 様</td></tr>
            <tr><th>ログインID</th><td class="mono">${esc(c.username)}</td></tr>
            <tr><th>パスワード</th><td class="mono">${esc(c.password)}</td></tr>
            <tr><th>ログインページ</th><td>www.yasamaru-portal.online</td></tr>
          </table>
        </div>
        <p>ログインIDとパスワードは、お客様専用の大切な情報です。第三者に知られないよう、お取り扱いにご注意ください。</p>
        <p>操作方法が分からない場合は、担当者までお気軽にお問い合わせください。</p>
        <div class="contact">
          <div>お問い合わせ先</div>
          <div>野菜の丸平／株式会社フレッシュエージェント</div>
          <div>TEL：03-3855-1610</div>
        </div>
        <p>今後とも、野菜の丸平をよろしくお願いいたします。</p>
      </section>
      <script>window.onload=function(){setTimeout(function(){window.print();},300);};</script>
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("ポップアップがブロックされました。許可してください"); return; }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  const reissueAndPrint = async (store) => {
    if (!window.confirm(`${store.name} のパスワードを新しく再発行して通知書を印刷します（電話番号の下4桁＋ランダム文字）。よろしいですか？`)) return;
    try {
      const res = await axios.post(`${API}/admin/stores/${store.id}/reissue-login`, {}, getAuthHeaders());
      printLoginLetter(res.data);
      toast.success("通知書を出力しました（パスワードを再発行しました）");
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "再発行に失敗しました");
    }
  };

  const openPwDialog = (store) => {
    const userInfo = storeUsers.find((u) => u.store_id === store.id);
    if (!userInfo || !userInfo.user_id) {
      toast.error("ログインユーザーが見つかりません");
      return;
    }
    setPwTarget({ store, userInfo });
    setPwValue("");
    setPwDialogOpen(true);
  };

  const handlePasswordReset = async () => {
    if (!pwValue) {
      toast.error("新しいパスワードを入力してください");
      return;
    }
    try {
      await axios.put(
        `${API}/users/${pwTarget.userInfo.user_id}/password`,
        { new_password: pwValue },
        getAuthHeaders()
      );
      toast.success("パスワードをリセットしました");
      setPwDialogOpen(false);
    } catch (error) {
      toast.error("パスワードリセットに失敗しました");
    }
  };

  if (loading) {
    return <div className="text-center py-8">読み込み中...</div>;
  }

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Store className="h-6 w-6 text-emerald-600" />
            店舗・ログイン管理
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            店舗情報・グループ本社・ログインIDとパスワードを管理します
          </p>
        </div>
        <Button
          onClick={openCreateDialog}
          className="bg-emerald-600 hover:bg-emerald-700"
        >
          <Plus className="h-4 w-4 mr-1" />
          納品先を追加
        </Button>
      </div>

      {/* テーブル */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left text-xs font-semibold text-slate-600 px-4 py-3">
                コード
              </th>
              <th className="text-left text-xs font-semibold text-slate-600 px-4 py-3">
                店舗名
              </th>
              <th className="text-left text-xs font-semibold text-slate-600 px-4 py-3">
                連絡先/配送先
              </th>
              <th className="text-left text-xs font-semibold text-slate-600 px-4 py-3">
                顧客ランク
              </th>
              <th className="text-left text-xs font-semibold text-slate-600 px-4 py-3">
                ログインID
              </th>
              <th className="text-right text-xs font-semibold text-slate-600 px-4 py-3">
                操作
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {stores.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center py-8 text-slate-500">
                  店舗がありません
                </td>
              </tr>
            ) : (
              stores.map((store) => {
                const userInfo = storeUsers.find((u) => u.store_id === store.id);
                return (
                  <tr key={store.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm font-mono text-slate-800">
                      {store.code}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-slate-800">
                      {store.name}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {store.delivery_partner && (
                        <div className="mb-1">{store.delivery_partner}</div>
                      )}
                      {store.address && (
                        <div className="text-xs text-slate-500">
                          〒{store.zip_code} {store.address}
                        </div>
                      )}
                      {store.tel && (
                        <div className="text-xs text-slate-500">TEL: {store.tel}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                        {store.customer_rank?.toUpperCase() || "A1"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {userInfo?.username ? (
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-emerald-600" />
                          <span className="font-mono text-slate-700">
                            {userInfo.username}
                          </span>
                        </div>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEditDialog(store)}
                          className="text-slate-600 hover:text-slate-800"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        {userInfo?.user_id && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openPwDialog(store)}
                            className="text-emerald-600 hover:text-emerald-700"
                            title="パスワードリセット"
                          >
                            <KeyRound className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => reissueAndPrint(store)}
                          className="text-slate-600 hover:text-emerald-700"
                          title="ログイン通知書を印刷（初期パスワード再発行）"
                          data-testid={`print-login-${store.code}`}
                        >
                          <Printer className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(store.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* 店舗作成・編集ダイアログ */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editing ? "店舗情報の編集" : "新しい納品先を追加"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>店舗名 *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="例: テスト店舗"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>コード *</Label>
                <Input
                  value={formData.code}
                  onChange={(e) =>
                    setFormData({ ...formData, code: e.target.value })
                  }
                  placeholder="例: 55001"
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>担当者名</Label>
                <Input
                  value={formData.contact_person}
                  onChange={(e) =>
                    setFormData({ ...formData, contact_person: e.target.value })
                  }
                  placeholder="例: 山田太郎"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>電話番号</Label>
                <Input
                  value={formData.tel}
                  onChange={(e) =>
                    setFormData({ ...formData, tel: e.target.value })
                  }
                  placeholder="例: 03-1234-5678"
                  className="mt-1"
                />
              </div>
            </div>

            {/* 多列モード設定 */}
            <div className="border-t pt-4 mt-4">
              <h3 className="font-semibold text-slate-700 mb-3">
                多列モード発注設定
              </h3>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="multi_column_mode"
                    checked={formData.multi_column_mode}
                    onChange={(e) =>
                      setFormData({ ...formData, multi_column_mode: e.target.checked })
                    }
                    className="w-4 h-4 text-emerald-600 rounded"
                  />
                  <Label htmlFor="multi_column_mode" className="cursor-pointer">
                    多列モード発注を有効にする
                  </Label>
                </div>
                {formData.multi_column_mode && (
                  <div>
                    <Label>列名（カンマ区切りで入力）</Label>
                    <Input
                      value={formData.column_names.join(",")}
                      onChange={(e) => {
                        const names = e.target.value.split(",").map(n => n.trim()).filter(n => n);
                        setFormData({ ...formData, column_names: names });
                      }}
                      placeholder="例: 職員,患者"
                      className="mt-1"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      ※発注画面で表示される列名を設定できます（例: 職員,患者,園児）
                    </p>
                    {formData.column_names.length > 0 && (
                      <div className="mt-2 flex gap-1 flex-wrap">
                        {formData.column_names.map((name, i) => (
                          <span key={i} className="px-2 py-1 bg-emerald-50 text-emerald-700 text-xs rounded">
                            {name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* 配送スケジュール例外設定 */}
            <div className="border-t pt-4 mt-4">
              <h3 className="font-semibold text-slate-700 mb-2">
                配送スケジュール例外設定
              </h3>
              <p className="text-xs text-slate-600 mb-4">
                すべての店舗は配送スケジュール通りに配送されますが、以下で例外を設定できます。
              </p>

              {/* 追加配送曜日 */}
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <Label className="text-sm font-semibold text-green-800 mb-2 block">
                  🟢 追加配送曜日
                </Label>
                <p className="text-xs text-green-700 mb-3">
                  配送スケジュールが休みの日でも、この店舗には配送する曜日
                </p>
                <div className="grid grid-cols-7 gap-2">
                  {['日', '月', '火', '水', '木', '金', '土'].map((day, index) => (
                    <label key={index} className="flex items-center gap-1.5 text-sm cursor-pointer bg-white p-2 rounded border border-green-200 hover:bg-green-100">
                      <input
                        type="checkbox"
                        checked={(formData?.additional_delivery_days || []).includes(index)}
                        onChange={(e) => {
                          const current = formData?.additional_delivery_days || [];
                          const updated = e.target.checked
                            ? [...current, index]
                            : current.filter(d => d !== index);
                          setFormData({ ...formData, additional_delivery_days: updated });
                        }}
                        className="rounded"
                      />
                      <span className="font-medium">{day}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* 配送休止曜日 */}
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <Label className="text-sm font-semibold text-red-800 mb-2 block">
                  🔴 配送休止曜日
                </Label>
                <p className="text-xs text-red-700 mb-3">
                  配送スケジュールが営業日でも、この店舗には配送しない曜日
                </p>
                <div className="grid grid-cols-7 gap-2">
                  {['日', '月', '火', '水', '木', '金', '土'].map((day, index) => (
                    <label key={index} className="flex items-center gap-1.5 text-sm cursor-pointer bg-white p-2 rounded border border-red-200 hover:bg-red-100">
                      <input
                        type="checkbox"
                        checked={(formData?.skip_delivery_days || []).includes(index)}
                        onChange={(e) => {
                          const current = formData?.skip_delivery_days || [];
                          const updated = e.target.checked
                            ? [...current, index]
                            : current.filter(d => d !== index);
                          setFormData({ ...formData, skip_delivery_days: updated });
                        }}
                        className="rounded"
                      />
                      <span className="font-medium">{day}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* 発注締切設定 */}
            <div className="border-t pt-4 mt-4">
              <h3 className="font-semibold text-slate-700 mb-3">
                発注締切設定
              </h3>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium">締切曜日</Label>
                    <select
                      value={formData.order_deadline_weekday}
                      onChange={(e) =>
                        setFormData({ ...formData, order_deadline_weekday: parseInt(e.target.value) })
                      }
                      className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm"
                    >
                      <option value={0}>月曜日</option>
                      <option value={1}>火曜日</option>
                      <option value={2}>水曜日</option>
                      <option value={3}>木曜日</option>
                      <option value={4}>金曜日</option>
                      <option value={5}>土曜日</option>
                      <option value={6}>日曜日</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">締切時刻</Label>
                    <Input
                      type="time"
                      value={formData.order_deadline_time}
                      onChange={(e) =>
                        setFormData({ ...formData, order_deadline_time: e.target.value })
                      }
                      className="mt-1"
                    />
                  </div>
                </div>
                <p className="text-xs text-slate-500">
                  基本設定: 前週の{["月", "火", "水", "木", "金", "土", "日"][formData.order_deadline_weekday]}曜日 {formData.order_deadline_time}が翌週分の発注締切
                </p>
              </div>
            </div>

            {/* 顧客ランク設定 */}
            <div className="border-t pt-4 mt-4">
              <h3 className="font-semibold text-slate-700 mb-3">価格設定</h3>
              <div>
                <Label>顧客ランク（価格帯） *</Label>
                <Select
                  value={formData.customer_rank}
                  onValueChange={(value) =>
                    setFormData({ ...formData, customer_rank: value })
                  }
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="ランクを選択" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="a1">A1ランク（最高価格）</SelectItem>
                    <SelectItem value="b2">B2ランク</SelectItem>
                    <SelectItem value="c3">C3ランク</SelectItem>
                    <SelectItem value="d4">D4ランク</SelectItem>
                    <SelectItem value="e5">E5ランク（最低価格）</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-2">
                  この店舗で表示される商品価格は、選択したランクに基づきます
                </p>
              </div>
            </div>

            {!editing && (
              <>
                <div className="border-t pt-4 mt-4">
                  <h3 className="font-semibold text-slate-700 mb-3">
                    ログインユーザー情報
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>ログインID</Label>
                      <Input
                        value={formData.username}
                        onChange={(e) =>
                          setFormData({ ...formData, username: e.target.value })
                        }
                        placeholder="例: 55001"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>パスワード</Label>
                      <Input
                        type="password"
                        value={formData.password}
                        onChange={(e) =>
                          setFormData({ ...formData, password: e.target.value })
                        }
                        placeholder="パスワード"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    ※ログイン情報は後からパスワードリセットで変更できます
                  </p>
                </div>
              </>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              キャンセル
            </Button>
            <Button onClick={handleSave} className="bg-emerald-600 hover:bg-emerald-700">
              {editing ? "更新" : "作成"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* パスワードリセットダイアログ */}
      <Dialog open={pwDialogOpen} onOpenChange={setPwDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>パスワードリセット</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <div className="text-sm text-slate-600 mb-4">
                {pwTarget?.store.name} のパスワードを変更します
              </div>
              <Label>新しいパスワード</Label>
              <Input
                type="password"
                value={pwValue}
                onChange={(e) => setPwValue(e.target.value)}
                placeholder="新しいパスワード"
                className="mt-1"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setPwDialogOpen(false)}>
              キャンセル
            </Button>
            <Button
              onClick={handlePasswordReset}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              リセット
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
